import { useEffect } from 'react';
import { useFieldValue } from '@atlassian/jira-issue-field-base';
import { SummaryKey } from '@atlassian/jira-providers-issue';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check';
import type { Card } from '../../../../model/types';
import { useBacklogActionCreator } from '../../../../state';
import { cardSummarySet } from '../../../../state/actions/summary';

type Props = {
	card: Card;
};

export const FieldSyncSummary = ({ card: { id, key, summary: summaryFromBacklog } }: Props) => {
	const setSummaryInBacklog = useBacklogActionCreator(cardSummarySet);
	const [summaryFromExtractedField, { setFieldValue: setSummaryInExtractedField }] = useFieldValue({
		issueKey: key,
		fieldKey: SummaryKey,
	});

	const summaryFromBacklogChanged = useDiffCheck([summaryFromBacklog]);
	const summaryFromExtractedFieldChanged = useDiffCheck([summaryFromExtractedField]);

	const storesNeedToSync = summaryFromBacklog !== summaryFromExtractedField;

	// Fixes creating issues from GIC where redux isn't synced with React Sweet
	// State. This is a workaround to unblock me. There is a more permanent
	// solution being investigated here
	// https://hello.atlassian.net/browse/DEE-923
	useEffect(() => {
		const summaryFromBacklogIsDefined = summaryFromBacklog !== undefined;
		const summaryFromExtractedFieldIsUndefined = summaryFromExtractedField === undefined;

		if (summaryFromBacklogIsDefined && summaryFromExtractedFieldIsUndefined) {
			setSummaryInExtractedField(key, SummaryKey, summaryFromBacklog);
		}
	}, [key, setSummaryInExtractedField, summaryFromBacklog, summaryFromExtractedField]);

	useEffect(() => {
		const summaryFromBacklogIsDefined = summaryFromBacklog !== undefined;

		if (summaryFromBacklogIsDefined && summaryFromBacklogChanged && storesNeedToSync) {
			setSummaryInExtractedField(key, SummaryKey, summaryFromBacklog);
		}
	}, [
		key,
		summaryFromBacklog,
		summaryFromBacklogChanged,
		storesNeedToSync,
		setSummaryInExtractedField,
	]);

	useEffect(() => {
		const summaryFromExtractedFieldIsDefined = summaryFromExtractedField !== undefined;

		if (
			!summaryFromBacklogChanged &&
			summaryFromExtractedFieldIsDefined &&
			summaryFromExtractedFieldChanged &&
			storesNeedToSync
		) {
			setSummaryInBacklog(id, summaryFromExtractedField);
		}
	}, [
		id,
		summaryFromBacklogChanged,
		summaryFromExtractedField,
		summaryFromExtractedFieldChanged,
		storesNeedToSync,
		setSummaryInBacklog,
	]);

	return null;
};

export default FieldSyncSummary;
